<script setup lang="ts">
import CoreSwitch from 'frontcore/views/switch/CoreSwitch.vue'
import i18n from 'frontcore/i18n'
import InputDate from 'frontcore/presentation/calendar/views/InputDate.vue'
import RelatedRemoteFiltersItem from 'frontcore/presentation/remote-filters/views/RelatedRemoteFiltersItem.vue'
import SegmentedButton from 'frontcore/views/segmented-button/SegmentedButton.vue'
import SelectMultiple from 'frontcore/views/select/multiple/SelectMultiple.vue'
import SelectSingle from 'frontcore/views/select/single/SelectSingle.vue'
import useRemoteFiltersStore from 'frontcore/presentation/remote-filters/data/use-remote-filters-store'
import { computed , ComputedRef } from 'vue'
import {
  RemoteFiltersBooleanValue,
  RemoteFiltersDateRangeValue,
  RemoteFiltersDateValue,
  RemoteFiltersOptionListValue,
  RemoteFiltersOptionValue,
  RemoteFiltersSegmentedButtonItemValue,
} from 'frontcore/presentation/remote-filters/domain/remote-filters-value'
import {
  RemoteFiltersConfiguration,
  RemoteFiltersFieldType,
} from 'frontcore/presentation/remote-filters/domain/types'

type Props = {
  configuration: RemoteFiltersConfiguration
  filterStoreID: string
  moduleRemoteFiltersConfigurations: RemoteFiltersConfiguration[]
}
const props = defineProps<Props>()

const { t } = i18n.global

const {
  getRemoteFiltersValue,
  setRemoteFiltersValue,
} = useRemoteFiltersStore(props.filterStoreID)

const values = computed(() => getRemoteFiltersValue(props.configuration.filterId)?.value)

const optionValueHandler = (value: number | string) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersOptionValue(props.configuration, value))
}

const optionListValueHandler = (value: (number | string)[]) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersOptionListValue(props.configuration, value))
}

const dateValueHandler = (value: Date) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersDateValue(props.configuration, value))
}

const dateRangeValueHandler = (value: Date[]) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersDateRangeValue(props.configuration, value))
}

const booleanValueHandler = (value: boolean) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersBooleanValue(props.configuration, value))
}

const segmentButtonValueHandler = (value: number) => {
  setRemoteFiltersValue(props.configuration.filterId, new RemoteFiltersSegmentedButtonItemValue(props.configuration, value))
}

const relatedFilter: ComputedRef<RemoteFiltersConfiguration | undefined> = computed(() => {
  return props.moduleRemoteFiltersConfigurations.find(config =>
    config.relatedFilters.includes(props.configuration.filterId)
  )
})

const componentClass = computed(() => {
  switch (props.configuration.filterType) {
  case RemoteFiltersFieldType.SELECT_SINGLE:
    return SelectSingle
  case RemoteFiltersFieldType.SELECT_MULTIPLE:
    return SelectMultiple
  case RemoteFiltersFieldType.DATE:
  case RemoteFiltersFieldType.DATE_RANGE:
    return InputDate
  case RemoteFiltersFieldType.SWITCH:
    return CoreSwitch
  case RemoteFiltersFieldType.SEGMENTED_BUTTON:
    return SegmentedButton
  }
})

const componentProps = computed(() => {
  switch (props.configuration.filterType) {
  case RemoteFiltersFieldType.SELECT_SINGLE:
  case RemoteFiltersFieldType.SELECT_MULTIPLE:
    return {
      dataTest: props.configuration.filterId,
      items: props.configuration.options,
      label: props.configuration.label,
      nullable: !props.configuration.required,
      placeholder: props.configuration.label,
      value: values.value,
      ...props.configuration.componentProps,
    }
  case RemoteFiltersFieldType.DATE:
    return {
      dataTest: props.configuration.filterId,
      label: props.configuration.label,
      nullable: !props.configuration.required,
      placeholder: t('common.messages.date'),
      value: values.value,
      ...props.configuration.componentProps,
    }
  case RemoteFiltersFieldType.DATE_RANGE:
    return {
      dataTest: props.configuration.filterId,
      label: props.configuration.label,
      nullable: !props.configuration.required,
      placeholder: t('common.messages.date_range'),
      range: true,
      value: values.value,
      ...props.configuration.componentProps,
    }
  case RemoteFiltersFieldType.SWITCH:
    return {
      dataTest: props.configuration.filterId,
      title: props.configuration.label,
      value: values.value,
      ...props.configuration.componentProps,
    }
  case RemoteFiltersFieldType.SEGMENTED_BUTTON:
    return {
      class: 'tw-mt-3',
      dataTest: props.configuration.filterId,
      items: props.configuration.options.map(option => ({id: option.id, label: option.text, disabled: option.disabled})),
      title: props.configuration.label,
      value: values.value,
      ...props.configuration.componentProps,
    }
  }
})

const componentListeners = computed(() => {
  switch (props.configuration.filterType) {
  case RemoteFiltersFieldType.SELECT_SINGLE:
  case RemoteFiltersFieldType.SELECT_SINGLE_REMOTE:
    return { input: optionValueHandler }
  case RemoteFiltersFieldType.SELECT_MULTIPLE:
  case RemoteFiltersFieldType.SELECT_MULTIPLE_REMOTE:
    return { input: optionListValueHandler }
  case RemoteFiltersFieldType.DATE:
    return { input: dateValueHandler }
  case RemoteFiltersFieldType.DATE_RANGE:
    return { input: dateRangeValueHandler }
  case RemoteFiltersFieldType.SWITCH:
    return { input: booleanValueHandler }
  case RemoteFiltersFieldType.SEGMENTED_BUTTON:
    return { input: segmentButtonValueHandler }
  }
})
</script>

<template>
  <div>
    <component
      :is="componentClass"
      v-bind="componentProps"
      v-on="componentListeners" />
    <RelatedRemoteFiltersItem
      v-if="relatedFilter"
      :key="relatedFilter.filterId"
      :configuration="relatedFilter"
      :moduleRemoteFiltersConfigurations="props.moduleRemoteFiltersConfigurations"
      :filterStoreID="props.filterStoreID" />
  </div>
</template>
